import type {
  ITaskLogEntry,
  IBlackboardEntry,
  IChecklistItemCompletion,
  ISchedulerComment,
  ISchedulerData,
  ISchedulerTask,
  IScheduleSummary,
  ITaskAttachment,
  TaskStatus,
  WritableLocationStatus,
} from '../interfaces/IScheduler.js';
import { throwForError, throwForNotImported } from './scheduleErrors.js';
import { checklistRefusal, tickRefusal } from './checklists.js';
import type { AuthedFetch } from './transport.js';

/** Wire shape of the immediate per-task progress write response. Progress
 *  travels alone now: the task's STATUS is a plan fact (`setTaskStatus`) and the
 *  activity's own status is Trusstor's. */
export interface ITaskProgressResponse {
  taskId: string;
  progressPercent: number;
}

/** One area's requested status, in an area write. */
export interface IAreaStatusChange {
  locationId: string;
  status: WritableLocationStatus;
}

/** What an area write left stored: the task % the server derived from the
 *  areas Trusstor took, and the fresh log. */
export interface IAreaStatusesResult {
  progressPercent: number;
  log: ITaskLogEntry[];
}

/** One committed task, as the commit route reports it. */
export interface ICommittedTask {
  taskId: string;
  /** `null` for an `administrative` commit — it has no Trusstor side. */
  trusstorGroupId: string | null;
  committedAt: string;
}

/** Why one task could not be committed (the 409's `details.rejections`). */
export type CommitRejectionReason =
  | 'not-a-leaf'
  | 'milestone'
  | 'blocked'
  | 'no-profession'
  | 'no-locations'
  | 'already-committed'
  | 'duplicate-name';

/** The commit request. `plan` carries only what the review changed. */
export interface ICommitTaskForm {
  dangerousActivity: boolean;
  assignee?: string | null;
  plan?: {
    kind?: ISchedulerTask['kind'];
    profession?: NonNullable<ISchedulerTask['profession']>;
    areaIds?: string[];
  };
}

/**
 * The drawer's per-task auto-save (2026-09-23 amendment): any subset of the
 * three fields, `null` clearing. Profession and areas are refused on a
 * committed task; the crew size never is.
 */
export interface ITaskPlanEdit {
  profession?: NonNullable<ISchedulerTask['profession']> | null;
  areaIds?: readonly string[];
  workerCount?: number | null;
}

/**
 * The schedule's version after the write, and whether this call was the one
 * that wrote it. `wrote` is `false` on a no-op — even when `version` already
 * sits one past what the caller last held, because that version may be a
 * concurrent editor's write the caller hasn't seen yet. Callers MUST branch
 * on `wrote`, not infer it from `version` alone.
 */
export interface IPlanWriteAck {
  version: string;
  wrote: boolean;
}

/** The per-task plan edit's answer. */
export type ITaskPlanEditResult = IPlanWriteAck;

/** A status write's answer: the status, plus the version it produced. */
export interface ITaskStatusResult extends IPlanWriteAck {
  status: TaskStatus;
}

export interface ICommitRejection {
  taskId: string;
  reason: CommitRejectionReason;
}

/**
 * The commit was refused and NOTHING was written — a parent commit validates
 * every leaf first, so `rejections` names every row to fix rather than the first
 * one hit. Thrown instead of a generic error so the form can list them.
 */
export class TaskCommitRejectedError extends Error {
  constructor(readonly rejections: ICommitRejection[]) {
    super('commit refused');
    this.name = 'TaskCommitRejectedError';
  }
}

/** One task's new inclusive span, `YYYY-MM-DD` both ends. */
export interface ITaskSpan {
  taskId: string;
  start: string;
  end: string;
}

/**
 * What finishing a task early or late did to the plan (2026-09-22 "done on the
 * day" amendment): the task's own span and any successors the user chose to
 * cascade, computed against `baseVersion`.
 */
export interface ITaskReschedule {
  baseVersion: string;
  tasks: ITaskSpan[];
}

/**
 * A reschedule was computed against a plan that has since moved (409
 * SCHEDULE_CONFLICT). Nothing was written: re-read the plan, recompute, and
 * ask again.
 */
export class TaskRescheduleConflictError extends Error {
  constructor() {
    super('the plan changed since the reschedule was computed');
    this.name = 'TaskRescheduleConflictError';
  }
}

/**
 * The schedule API both clients share — the reads plus the granular execution
 * writes — over an injected transport: each app binds its own `authedFetch` in
 * its `apis/schedule.ts` and re-exports the result. The scheduler-only
 * operations (save, import, delete, push-to-Genba, structural edits) stay in
 * `frontend/src/apis/schedule.ts`.
 */
export function createScheduleApi(authedFetch: AuthedFetch) {
  /** List a site's schedules (the picker source), most-recently-updated first. */
  async function fetchSchedules(siteId: string, signal?: AbortSignal): Promise<IScheduleSummary[]> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/schedules`, { signal });
    if (res.ok) {
      const body = (await res.json()) as { schedules: IScheduleSummary[] };
      return body.schedules;
    }
    return throwForError(res, 'list schedules');
  }

  /**
   * Load one schedule (Gantt) by its scheduleNumber, in ISchedulerData shape.
   * Resolves from the D-ORCA DB — a schedule that was never imported throws
   * `ScheduleNotImportedError` (409 SCHEDULE_NOT_IMPORTED) so the page can offer
   * a one-click import instead of a generic load error.
   */
  async function fetchSchedule(
    siteId: string,
    scheduleNumber: string,
    signal?: AbortSignal,
  ): Promise<ISchedulerData> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}`,
      { signal },
    );
    if (res.ok) {
      const data = (await res.json()) as ISchedulerData;
      if (typeof data.version !== 'string' || data.version === '') {
        throw new Error(
          'getSchedule: response is missing a valid version token — ' +
          'the backend shape may have changed. Save is blocked until a fresh load.',
        );
      }
      return data;
    }
    const notImported = await throwForNotImported(res, scheduleNumber);
    if (notImported) throw notImported;
    return throwForError(res, 'load schedule');
  }

  /**
   * Immediate per-task progress write (the Overview slider), decoupled from the
   * whole-schedule Save and without bumping the schedule version.
   *
   * Only a COMMITTED task can report progress (2026-09-17 task-commit design,
   * invariant 2) — an uncommitted one answers 409 TASK_NOT_COMMITTED, which the
   * caller surfaces as "commit this task first" rather than a generic failure.
   * The execution log is server-generated (invariant 9), so this request carries
   * no log; the store mirrors the same entry optimistically.
   */
  async function updateTaskProgress(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    progressPercent: number,
  ): Promise<{ progressPercent: number }> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/progress`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ progressPercent }),
      },
    );
    if (res.ok) {
      const body = (await res.json()) as ITaskProgressResponse;
      return { progressPercent: body.progressPercent };
    }
    return throwForError(res, 'update task progress');
  }

  // ---------------------------------------------------------------------------
  // Phase-2 GRANULAR execution writes. Each targets ONE item (an area, a comment,
  // a checklist item) so concurrent field users editing different items can't
  // erase each other. The execution log is server-generated, so it rides the
  // RESPONSE (`log`), never the request. These return the server's fresh log
  // so a caller MAY reconcile the timeline; the current callers rely on the
  // optimistic store between loads (like progress/locations) and just persist.
  // ---------------------------------------------------------------------------

  /**
   * Report areas done, ready for review, or neither (`planned`, taking either
   * back) — ONE request however many areas (a sweep, batched taps). The
   * authoritative write goes to TRUSSTOR (the area sequence items), so only
   * these values exist here; `inProgress`/`delayed`/`overdue` are Trusstor's
   * own computation from tag work time.
   *
   * The server derives the task % the areas move and stores it after them
   * (QA R-04) — never PUT it beside this. `holdProgress`: the session's % was
   * set by hand, so the areas don't move it. Resolves to the stored % and the
   * fresh log. A take-back on an activity Trusstor holds as complete is a 409
   * `AREA_GROUP_COMPLETE`.
   *
   * Area ASSIGNMENT is NOT here: it is a plan fact, saved by `editTaskPlan`.
   */
  async function setTaskAreaStatuses(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    changes: readonly IAreaStatusChange[],
    options: { holdProgress: boolean },
  ): Promise<IAreaStatusesResult> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/locations`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changes, holdProgress: options.holdProgress }),
      },
    );
    if (res.ok) {
      const body = (await res.json()) as IAreaStatusesResult & { taskId: string };
      return { progressPercent: body.progressPercent, log: body.log };
    }
    return throwForError(res, 'set task area statuses');
  }

  /**
   * COMMIT a task — the act that makes it an operational promise. A leaf commits
   * itself; a PARENT commits every leaf descendant, all-or-nothing. A refusal is
   * a {@link TaskCommitRejectedError} naming every offending task. `plan` is
   * what the review filled in on the way (2026-09-18 commit UX design): the
   * commit writes it to the task before making the promise.
   */
  async function commitTask(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    form: ICommitTaskForm,
  ): Promise<ICommittedTask[]> {
    return postCommit(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/commit`,
      form,
    );
  }

  /** COMMIT a chosen SET of leaves, each with its own form — the list-form
   *  review. All-or-nothing over the set, like a parent commit. */
  async function commitTasks(
    siteId: string,
    scheduleNumber: string,
    tasks: readonly (ICommitTaskForm & { taskId: string })[],
  ): Promise<ICommittedTask[]> {
    return postCommit(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/commit`,
      { tasks },
    );
  }

  async function postCommit(url: string, body: unknown): Promise<ICommittedTask[]> {
    const res = await authedFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (res.ok) {
      const payload = (await res.json()) as { committed: ICommittedTask[] };
      return payload.committed;
    }
    const failure = await res.clone().json().catch(() => null);
    if (failure?.error?.code === 'COMMIT_REJECTED') {
      throw new TaskCommitRejectedError(failure.error.details?.rejections ?? []);
    }
    return throwForError(res, 'commit task');
  }

  /** UNCOMMIT — allowed only while the activity has no recorded work time;
   *  past that the exits are "mark it done" or deleting the task (409). */
  async function uncommitTask(siteId: string, scheduleNumber: string, taskId: string): Promise<void> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/commit`,
      { method: 'DELETE' },
    );
    if (res.ok) return;
    return throwForError(res, 'uncommit task');
  }

  /** Set the task's PLAN status: mark done, block, or reopen/unblock. `done`
   *  also completes the Trusstor group of a committed field task, and may
   *  carry the dates finishing early or late moved (`reschedule`). */
  async function setTaskStatus(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    status: TaskStatus,
    reschedule?: ITaskReschedule,
  ): Promise<ITaskStatusResult> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/status`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reschedule ? { status, reschedule } : { status }),
      },
    );
    if (res.ok) {
      const { status: set, version, wrote } = (await res.json()) as ITaskStatusResult;
      return { status: set, version, wrote };
    }
    const refusal = await checklistRefusal(res);
    if (refusal) throw refusal;
    if (res.status === 409) {
      const failure = await res.clone().json().catch(() => null);
      if (failure?.error?.code === 'SCHEDULE_CONFLICT') throw new TaskRescheduleConflictError();
    }
    return throwForError(res, 'set task status');
  }

  /** Add a comment (idempotent by the client-generated id → safe retry). Returns
   *  the task's full comments array. */
  async function addTaskComment(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    input: { id: string; text: string; attachments?: ITaskAttachment[]; authorName?: string },
  ): Promise<ISchedulerComment[]> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/comments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: input.id, text: input.text, attachments: input.attachments ?? [], ...(input.authorName ? { authorName: input.authorName } : {}) }),
      },
    );
    if (res.ok) {
      const body = (await res.json()) as { taskId: string; comments: ISchedulerComment[]; log?: ITaskLogEntry[] };
      return body.comments;
    }
    return throwForError(res, 'add task comment');
  }

  /** Delete a comment by id (idempotent; not logged). Returns the remaining comments. */
  async function deleteTaskComment(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    commentId: string,
  ): Promise<ISchedulerComment[]> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`,
      { method: 'DELETE' },
    );
    if (res.ok) {
      const body = (await res.json()) as { taskId: string; comments: ISchedulerComment[] };
      return body.comments;
    }
    return throwForError(res, 'delete task comment');
  }

  /** Complete / un-complete one checklist item. `evidence` carries the client photo/
   *  comment/blackboard; the tick metadata (checkedAt/By) is server-filled. Returns
   *  the task's full `checklistCompletion` map. */
  async function setTaskCheck(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    itemId: string,
    input: { completed: boolean; evidence?: { attachedPhoto?: ITaskAttachment; comment?: string; blackboard?: IBlackboardEntry } },
  ): Promise<Record<string, IChecklistItemCompletion>> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/checks/${encodeURIComponent(itemId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input.evidence ? { completed: input.completed, evidence: input.evidence } : { completed: input.completed }),
      },
    );
    if (res.ok) {
      const body = (await res.json()) as { taskId: string; checklistCompletion: Record<string, IChecklistItemCompletion>; log: ITaskLogEntry[] };
      return body.checklistCompletion;
    }
    const refused = await tickRefusal(res);
    if (refused) throw refused;
    return throwForError(res, 'set task check');
  }

  /**
   * Save a task's profession / areas / crew size as they are edited. Resolves
   * with the schedule version after the write and whether this call was the
   * one that wrote it — `wrote: false` when there was nothing to write, even
   * if `version` already sits one past what the caller last held (that step
   * may belong to a concurrent editor the caller hasn't seen yet).
   */
  async function editTaskPlan(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    edit: ITaskPlanEdit,
  ): Promise<ITaskPlanEditResult> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/plan`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(edit) },
    );
    if (res.ok) {
      const body = (await res.json()) as { taskId: string; version: string; wrote: boolean };
      return { version: body.version, wrote: body.wrote };
    }
    return throwForError(res, 'edit task plan');
  }

  return {
    fetchSchedules,
    fetchSchedule,
    updateTaskProgress,
    setTaskAreaStatuses,
    commitTask,
    commitTasks,
    uncommitTask,
    setTaskStatus,
    editTaskPlan,
    addTaskComment,
    deleteTaskComment,
    setTaskCheck,
  };
}
